import React, { useEffect, useRef, useState } from 'react';
import { fetchMicrosoftOIDCStatus, login, microsoftOIDCErrorMessage } from '../lib/api';

interface Props {
  onLogin: () => void | Promise<void>;
  onRegister?: () => void;
  errorMessage?: string;
}

export default function LoginPage({ onLogin, onRegister, errorMessage }: Props) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(() =>
    microsoftOIDCErrorMessage(new URLSearchParams(window.location.search).get('oidc_error')),
  );
  const [loading, setLoading] = useState(false);
  const [microsoftEnabled, setMicrosoftEnabled] = useState(false);
  const errorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let live = true;
    void fetchMicrosoftOIDCStatus()
      .then((status) => {
        if (live) setMicrosoftEnabled(status.enabled);
      })
      .catch(() => {
        if (live) setMicrosoftEnabled(false);
      });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    if (error) errorRef.current?.focus();
  }, [error]);

  useEffect(() => {
    if (errorMessage) setError(errorMessage);
  }, [errorMessage]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password || loading) return;
    setLoading(true);
    setError('');
    try {
      await login(email, password);
      await onLogin();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <h1 className="login-title">Borgee</h1>
        {microsoftEnabled && (
          <a className="btn microsoft-auth-btn" href={`/api/auth/microsoft/start?return_to=${encodeURIComponent(new URLSearchParams(window.location.search).get('return_to') === '/invite' ? '/invite' : '/')}`}>
            <svg
              className="microsoft-logo"
              width="18"
              height="18"
              viewBox="0 0 21 21"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
              focusable="false"
            >
              <rect x="1" y="1" width="9" height="9" fill="#F25022" />
              <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
              <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
              <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
            </svg>
            Continue with Microsoft
          </a>
        )}
        {microsoftEnabled && (
          <div className="auth-divider" aria-hidden="true">
            <span>or</span>
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <label htmlFor="email" className="sr-only">
            Email
          </label>
          <input
            id="email"
            name="email"
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input-field login-input"
            autoComplete="email"
            autoFocus
          />
          <label htmlFor="password" className="sr-only">
            Password
          </label>
          <input
            id="password"
            name="password"
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input-field login-input"
            autoComplete="current-password"
          />
          {error && (
            <div className="login-error" role="alert" tabIndex={-1} ref={errorRef}>
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={loading || !email || !password}
            className="btn btn-primary login-btn"
          >
            {loading ? 'Logging in...' : 'Log in'}
          </button>
        </form>
        {onRegister && (
          <div className="register-link">
            <a onClick={onRegister}>Have an invite code? Register</a>
          </div>
        )}
      </div>
    </div>
  );
}
